import { Country } from "@shared/types/commonTypes";
import { useParams } from "react-router-dom";
import { useCountries } from "./useCountries";
import { useGetSingleCompanyDetails } from "./useGetSingleCompanyDetails";

/**
 * Unified hook to get the current company's country.
 * Works for both clinics (via clinicId) and event organizers (via eventOrganizerId).
 * Automatically detects which type based on URL params.
 * 
 * Uses useGetSingleCompanyDetails which will fetch the company data if not already loaded.
 */
export const useCurrentCompanyCountry = (): Country | null => {
  const { clinicId, eventOrganizerId } = useParams();
  const { countries, isLoading: isCountriesLoading } = useCountries();

  // Determine which company ID to use
  const companyId = clinicId || eventOrganizerId;

  // Get company data - useGetSingleCompanyDetails will fetch if needed
  const { company } = useGetSingleCompanyDetails(companyId, {
    enabled: Boolean(companyId),
  });

  if (!company || !company.address?.countryISO3) {
    return null;
  }

  // Wait for countries to load before trying to find the country
  if (isCountriesLoading || countries.length === 0) {
    return null;
  }

  // Find the full country object
  const country = countries.find(
    (c) => c.iso3 === company.address.countryISO3
  );

  return country ?? null;
};
